import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import AnimatedScore from '../components/AnimatedScore';
import { 
  ArrowLeft, 
  Sparkles, 
  AlertTriangle, 
  Share2, 
  Clock, 
  Hand, 
  Star, 
  ChevronDown, 
  ChevronUp,
  Footprints,
  Download,
  Heart,
  Activity,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_661e4868-d868-458c-86d6-2d10e12396a2/artifacts/tx0slj8p_ChatGPT_Image_15_de_mai._de_2026__13_56_39-removebg-preview.png";

const DISCLAIMER = "Este conteúdo é educacional e voltado ao bem-estar. A leitura reflexológica não constitui diagnóstico médico, não substitui consulta com profissional de saúde e não deve ser usada para decisões clínicas.";

const ReadingResult = () => {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [reading, setReading] = useState(null);
  const [footImage, setFootImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedZone, setSelectedZone] = useState(null);
  const [error, setError] = useState('');
  const [showMassageProtocol, setShowMassageProtocol] = useState(false);
  const [expandedTechnique, setExpandedTechnique] = useState(null);

  useEffect(() => {
    fetchReading();
  }, [id]);

  const fetchReading = async () => {
    try {
      const [readingRes, imageRes] = await Promise.all([
        api.get(`/readings/${id}`),
        api.get(`/readings/${id}/image`).catch(() => null)
      ]);
      
      setReading(readingRes.data.reading);
      if (imageRes?.data?.image) {
        setFootImage(imageRes.data.image);
      }
    } catch (err) {
      console.error('Error fetching reading:', err);
      setError('Leitura não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReactivityLevel = (score) => {
    if (score >= 0.7) return { label: 'Alta', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200' };
    if (score >= 0.4) return { label: 'Média', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200' };
    return { label: 'Baixa', color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' };
  };

  const getPressureLabel = (pressure) => {
    const labels = {
      'leve': { text: 'Leve', color: 'bg-green-100 text-green-700' },
      'média': { text: 'Média', color: 'bg-amber-100 text-amber-700' },
      'firme': { text: 'Firme', color: 'bg-orange-100 text-orange-700' },
      'média-firme': { text: 'Média-Firme', color: 'bg-orange-100 text-orange-700' }
    };
    return labels[pressure] || labels['média'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center animate-fade-in-up">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2E5C55]/10 to-[#4DB6AC]/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Sparkles className="h-10 w-10 text-[#2E5C55]" />
          </div>
          <p className="text-[#8E8E93] font-medium">Carregando sua leitura...</p>
        </div>
      </div>
    );
  }

  if (error || !reading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-4 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 text-center max-w-md shadow-xl border border-[#E8E4D9]/50 animate-fade-in-up">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          </div>
          <h2 className="font-bold text-xl text-[#1C1C1E] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Oops!</h2>
          <p className="text-[#8E8E93] mb-6">{error || 'Leitura não encontrada'}</p>
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="bg-gradient-to-r from-[#2E5C55] to-[#3A7068] text-white rounded-full px-8"
          >
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-28" style={{ background: 'linear-gradient(180deg, #FDF9F3 0%, #F8F5EF 100%)' }}>
      {/* Header */}
      <header
        className="px-4 py-5 flex items-center justify-between relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at top right, #0F766E 0%, #083D3A 60%, #0A0F14 100%)',
        }}
      >
        <div className="absolute inset-0 scanner-grid opacity-25 pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-cyan-300">Scan Concluído</p>
            <h1 className="font-bold text-lg text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
              Seu Mapa Reflexo
            </h1>
          </div>
        </div>
        <button className="relative z-10 p-2 text-white/80 hover:text-cyan-300 transition-colors rounded-xl hover:bg-white/5">
          <Share2 className="h-5 w-5" />
        </button>
      </header>

      {/* Main Content */}
      <main className="px-4 -mt-2 max-w-lg mx-auto">
        {/* Date Badge */}
        <div className="flex justify-center mb-4">
          <div className="bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-sm border border-[#E8E4D9]/50 text-sm text-[#48484A]">
            {formatDate(reading.created_at)}
          </div>
        </div>

        {/* Generated Map Card */}
        <section className="mb-6 animate-fade-in-up">
          <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-[#E8E4D9]/50">
            <div className="p-6 bg-gradient-to-r from-[#2E5C55]/5 to-[#4DB6AC]/5 border-b border-[#E8E4D9]/50">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center">
                  <Footprints className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-lg text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Mapa de Reflexologia
                  </h2>
                  <p className="text-sm text-[#8E8E93]">Mapa gerado por IA com suas zonas analisadas</p>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              {reading.generated_map_base64 ? (
                <img 
                  src={`data:image/png;base64,${reading.generated_map_base64}`}
                  alt="Mapa de Reflexologia Podal"
                  className="w-full rounded-2xl shadow-md"
                  style={{ maxHeight: '600px', objectFit: 'contain' }}
                  data-testid="generated-foot-map"
                />
              ) : (
                <div className="bg-[#FDFBF7] rounded-2xl p-8 text-center">
                  <Sparkles className="h-12 w-12 text-[#2E5C55]/30 mx-auto mb-3 animate-pulse" />
                  <p className="text-[#8E8E93]">Mapa visual sendo processado...</p>
                </div>
              )}
            </div>

            {/* Reactivity Score */}
            <div className="p-4 border-t border-[#E8E4D9]/50 flex items-center justify-between bg-[#FDFBF7]">
              <span className="text-sm text-[#8E8E93] font-medium">Score de Reatividade Geral</span>
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1 rounded-full text-sm font-bold ${getReactivityLevel(reading.reactivity_score).bg} ${getReactivityLevel(reading.reactivity_score).color}`}>
                  {Math.round(reading.reactivity_score * 100)}%
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* General Observation */}
        {reading.general_observation && (
          <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="bg-white rounded-3xl p-6 shadow-lg border border-[#E8E4D9]/50">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#4DB6AC]/10 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-[#4DB6AC]" />
                </div>
                <h3 className="font-bold text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Análise Geral
                </h3>
              </div>
              <p className="text-[#48484A] leading-relaxed">
                {reading.general_observation}
              </p>
            </div>
          </section>
        )}

        {/* Selected Zone Detail */}
        {selectedZone && (
          <section className="mb-6 animate-fade-in-up">
            <div 
              className="bg-white rounded-3xl p-6 shadow-lg border-l-4"
              style={{ borderLeftColor: selectedZone.color }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: selectedZone.color }}
                  >
                    {selectedZone.is_priority && (
                      <Star className="h-5 w-5 text-white fill-white" />
                    )}
                  </div>
                  <h3 className="font-bold text-[#1C1C1E]" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {selectedZone.name}
                  </h3>
                </div>
                <span className={`text-xs px-3 py-1.5 rounded-full font-bold ${getReactivityLevel(selectedZone.reactivity).bg} ${getReactivityLevel(selectedZone.reactivity).color}`}>
                  {Math.round(selectedZone.reactivity * 100)}%
                </span>
              </div>
              
              {/* Recommendations */}
              {selectedZone.recommendations?.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-[#8E8E93] mb-3 font-medium">Recomendações de bem-estar:</p>
                  <ul className="space-y-2">
                    {selectedZone.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-[#48484A] bg-[#FDFBF7] p-3 rounded-xl">
                        <Sparkles className="h-4 w-4 text-[#D4A373] flex-shrink-0 mt-0.5" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Massage Techniques */}
              {selectedZone.massage_techniques?.length > 0 && (
                <div>
                  <p className="text-sm text-[#8E8E93] mb-3 font-medium flex items-center gap-2">
                    <Hand className="h-4 w-4" />
                    Técnicas de massagem:
                  </p>
                  <div className="space-y-2">
                    {selectedZone.massage_techniques.map((tech, idx) => (
                      <div key={idx} className="bg-[#FDFBF7] rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-sm text-[#1C1C1E]">{tech.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#8E8E93] flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {tech.duration}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPressureLabel(tech.pressure).color}`}>
                              {getPressureLabel(tech.pressure).text}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-[#48484A]">{tech.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <button 
                onClick={() => setSelectedZone(null)}
                className="mt-4 text-sm text-[#2E5C55] font-medium hover:text-[#1A3A35]"
              >
                Fechar detalhes
              </button>
            </div>
          </section>
        )}

        {/* Zones Grid */}
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Zonas Analisadas ({reading.zones?.length || 0})
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {reading.zones?.map((zone) => {
              const reactivity = getReactivityLevel(zone.reactivity);
              const isSelected = selectedZone?.key === zone.key;
              return (
                <button
                  key={zone.key}
                  onClick={() => setSelectedZone(isSelected ? null : zone)}
                  className={`bg-white rounded-2xl p-4 text-left transition-all shadow-sm border ${
                    isSelected 
                      ? 'ring-2 ring-[#2E5C55] shadow-lg' 
                      : 'border-[#E8E4D9]/50 hover:shadow-md hover:-translate-y-0.5'
                  }`}
                  data-testid={`zone-card-${zone.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: zone.color }}
                    >
                      {zone.is_priority && (
                        <Star className="h-4 w-4 text-white fill-white" />
                      )}
                    </div>
                    <span className={`text-xs font-bold ${reactivity.color}`}>
                      {Math.round(zone.reactivity * 100)}%
                    </span>
                  </div>
                  <p className="font-semibold text-sm text-[#1C1C1E] mb-1">{zone.name}</p>
                  <p className={`text-xs ${reactivity.color}`}>
                    Reatividade {reactivity.label.toLowerCase()}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Massage Protocol */}
        {reading.massage_protocol?.length > 0 && (
          <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <button 
              onClick={() => setShowMassageProtocol(!showMassageProtocol)}
              className="w-full bg-gradient-to-r from-[#2E5C55] to-[#3A7068] rounded-2xl p-5 flex items-center justify-between shadow-lg"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Hand className="h-6 w-6 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Protocolo de Massagem
                  </h2>
                  <p className="text-sm text-white/70">
                    {reading.massage_protocol.length} zonas prioritárias
                  </p>
                </div>
              </div>
              {showMassageProtocol ? (
                <ChevronUp className="h-6 w-6 text-white" />
              ) : (
                <ChevronDown className="h-6 w-6 text-white" />
              )}
            </button>

            {showMassageProtocol && (
              <div className="mt-4 space-y-3 animate-fade-in-up">
                {reading.massage_protocol.map((protocol, idx) => (
                  <div key={protocol.zone_key} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-[#E8E4D9]/50">
                    <button
                      onClick={() => setExpandedTechnique(expandedTechnique === idx ? null : idx)}
                      className="w-full p-4 flex items-center justify-between hover:bg-[#FDFBF7] transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full gradient-primary text-white flex items-center justify-center font-bold">
                          {idx + 1}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#1C1C1E]">{protocol.zone}</span>
                            {protocol.is_priority && (
                              <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                            )}
                          </div>
                          <span className={`text-xs font-medium ${getReactivityLevel(protocol.reactivity).color}`}>
                            {Math.round(protocol.reactivity * 100)}% reativo
                          </span>
                        </div>
                      </div>
                      {expandedTechnique === idx ? (
                        <ChevronUp className="h-5 w-5 text-[#8E8E93]" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-[#8E8E93]" />
                      )}
                    </button>

                    {expandedTechnique === idx && (
                      <div className="px-4 pb-4 space-y-3 border-t border-[#E8E4D9]/50 pt-4 animate-fade-in-up">
                        {protocol.techniques.map((tech, techIdx) => (
                          <div key={techIdx} className="bg-[#FDFBF7] rounded-xl p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-[#1C1C1E] text-sm">{tech.name}</h4>
                              <span className={`text-xs px-2 py-1 rounded-full ${getPressureLabel(tech.pressure).color}`}>
                                {getPressureLabel(tech.pressure).text}
                              </span>
                            </div>
                            <p className="text-sm text-[#48484A] mb-2">{tech.description}</p>
                            <div className="flex items-center gap-1 text-xs text-[#8E8E93]">
                              <Clock className="h-3 w-3" />
                              <span>Duração: {tech.duration}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* General Recommendations */}
        {reading.recommendations?.length > 0 && (
          <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <h2 className="font-bold text-lg text-[#1C1C1E] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Recomendações Gerais
            </h2>
            <div className="bg-white rounded-3xl p-6 shadow-lg border border-[#E8E4D9]/50">
              <ul className="space-y-4">
                {reading.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-4 text-[#48484A]">
                    <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-white">{idx + 1}</span>
                    </div>
                    <p className="pt-1">{rec}</p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <section className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <div className="p-5 bg-amber-50 rounded-2xl border-l-4 border-amber-400">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{DISCLAIMER}</p>
            </div>
          </div>
        </section>

        {/* Actions */}
        <section className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <Button 
            onClick={() => navigate('/nova-leitura')}
            className="w-full h-14 text-white rounded-2xl font-bold transition-all hover:shadow-xl"
            style={{ background: 'linear-gradient(135deg, #083D3A 0%, #0F766E 50%, #14B8A6 100%)', boxShadow: '0 8px 30px rgba(15,118,110,0.35)' }}
            data-testid="new-reading-btn"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Iniciar nova leitura
          </Button>
        </section>
      </main>
      <FloatingNav />
    </div>
  );
};

export default ReadingResult;
